import React from "react";
import { View, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ShiftForm, Position, MealOption } from "./types";
import { PositionCard } from "./PositionCard";
import { TreeNode } from "@/components/ui/TreeNode";
import { MealOptionEditor } from "@/components/screens/shared";
import { Menu } from "@/components/Menu";
import { ShiftTimeInput } from "@/components/experimental/ShiftTimeInput";
import { LookupInput, type LookupInputOption } from "@/components/ui/LookupInput";

interface ShiftCardProps {
  shift: ShiftForm;
  index: number;
  updateShift: (index: number, patch: Partial<ShiftForm>) => void;
  removeShift: (index: number) => void;
  duplicateShift: (shiftIndex: number) => void;
  addPosition: (shiftIndex: number) => void;
  removePosition: (shiftIndex: number, positionIndex: number) => void;
  duplicatePosition: (shiftIndex: number, positionIndex: number) => void;
  updatePosition: (shiftIndex: number, positionIndex: number, patch: Partial<Position>) => void;
  addMealOption: (shiftIndex: number) => void;
  removeMealOption: (shiftIndex: number, mealIndex: number) => void;
  updateMealOption: (shiftIndex: number, mealIndex: number, patch: Partial<MealOption>) => void;
  /** Project-level location for inheritance */
  projectLocation?: {
    id: string;
    name: string;
    address?: { street?: string; city?: string; state?: string; zip?: string };
  } | null;
  /** Location options for the LookupInput */
  locationOptions?: LookupInputOption[];
  /** Called when shift location changes via LookupInput */
  onLocationChange?: (shiftIndex: number, locationId: string | null) => void;
  /** Called when a position's location override changes */
  onPositionLocationChange?: (shiftIndex: number, positionIndex: number, locationId: string | null) => void;
}

export function ShiftCard({
  shift,
  index,
  updateShift,
  removeShift,
  duplicateShift,
  addPosition,
  removePosition,
  duplicatePosition,
  updatePosition,
  addMealOption,
  removeMealOption,
  updateMealOption,
  projectLocation,
  locationOptions = [],
  onLocationChange,
  onPositionLocationChange,
}: ShiftCardProps) {
  const { colors } = useTheme();

  return (
    <View key={index} style={{ gap: 8 }}>
      <View
        style={{
          gap: 12,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
          padding: 16,
          borderRadius: 12,
          backgroundColor: colors.card,
        }}
      >
        {/* Shift header row: Name, Description, Menu */}
        <View style={{ flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
          <View style={{ flexDirection: "row", flex: 1, gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <ThemedInput
              testID={`shift-name-input-${index}`}
              placeholder={`Shift ${index + 1} name`}
              value={shift.name ?? ""}
              onChangeText={(v) => updateShift(index, { name: v })}
              style={{ flex: 0, flexGrow: 1, minWidth: 240 }}
              inputStyle={{ backgroundColor: colors.backgroundSecondary }}
            />
            <ThemedInput
              testID={`shift-description-input-${index}`}
              placeholder="Description"
              value={shift.description ?? ""}
              onChangeText={(v) => updateShift(index, { description: v })}
              style={{ flex: 2, minWidth: 200 }}
              inputStyle={{ backgroundColor: colors.backgroundSecondary }}
            />
          </View>
          <Menu
            buttonStyle={{
              marginLeft: "auto",
              alignSelf: "flex-start",
              borderWidth: 0,
              backgroundColor: "transparent",
            }}
            hideArchive={true}
            hideEdit={true}
            canDuplicate={true}
            canDelete={true}
            onDuplicate={() => duplicateShift(index)}
            onDelete={() => removeShift(index)}
          />
        </View>
        <ShiftTimeInput
          label="Schedule"
          startTime={shift.start_time ?? null}
          endTime={shift.end_time ?? null}
          editing
          onChange={(values) => {
            updateShift(index, { start_time: values.startTime, end_time: values.endTime });
          }}
        />

        {/* Location Selection for Shift */}
        <LookupInput
          label="Location"
          value={shift.meta_data?.location?.id || null}
          options={locationOptions}
          state="editing"
          icon="map-marker"
          placeholder={
            !shift.meta_data?.location && projectLocation?.name ? projectLocation.name : "No location selected"
          }
          info={!shift.meta_data?.location && projectLocation ? "From Project" : undefined}
          onChange={(locationId) => onLocationChange?.(index, locationId)}
        />

        {/* Meal Option Section */}
        <View style={{ gap: 6, marginTop: 12 }}>
          <ThemedText style={{ fontSize: 13, fontWeight: "600", opacity: 0.8 }}>Meal Breaks</ThemedText>
          <MealOptionEditor
            items={shift.meta_data?.meal_option || []}
            editing={true}
            onAdd={() => addMealOption(index)}
            onRemove={(mealIndex) => removeMealOption(index, mealIndex)}
            onUpdate={(mealIndex, patch) => updateMealOption(index, mealIndex, patch)}
          />
        </View>
      </View>

      <View style={{ flexDirection: "row", justifyContent: "flex-start", alignItems: "center" }}>
        <ThemedText style={{ fontSize: 13, fontWeight: "600", opacity: 0.8 }}>
          Positions{shift.positions.length ? ` (${shift.positions.length})` : ""}
        </ThemedText>
      </View>

      {/* Positions Tree */}
      <View style={{ gap: 12 }}>
        {shift.positions?.length
          ? shift.positions.map((_, c) => (
              <TreeNode gap={12} key={c} depth={1} isLast={false}>
                <PositionCard
                  position={shift.positions[c]}
                  shiftIndex={index}
                  positionIndex={c}
                  shift={shift}
                  updatePosition={updatePosition}
                  removePosition={removePosition}
                  duplicatePosition={duplicatePosition}
                  locationOptions={locationOptions}
                  onLocationChange={onPositionLocationChange}
                />
              </TreeNode>
            ))
          : null}

        <TreeNode gap={12} depth={1} isLast isAddButton>
          <Button
            id={`add-position-button-${index}`}
            icon="plus"
            text="Add Position"
            iconPosition="right"
            iconColor="white"
            style={{
              height: 35,
              backgroundColor: colors.primary,
              paddingHorizontal: 16,
            }}
            textStyle={{ color: "white" }}
            onPress={() => addPosition(index)}
          />
        </TreeNode>
      </View>
    </View>
  );
}
